import { FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, PauseCircle, Pencil, PlayCircle, Plus, ShoppingCart, Trash2, XCircle } from 'lucide-react';
import { CancelOrderDialog, EditOrderDialog, HoldDialog } from './OrderActions';
import { formatDate, formatMoney, formatQty, type Paginated, SALES_ORDER_STATUSES, SALES_ORDER_STATUS_LABELS } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { cuerpoPedidoVenta } from '@/lib/order-body';
import { useAuth } from '@/lib/auth';
import { useDebounced } from '@/hooks/useDebounced';
import { PageHeader, SearchInput, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/use-confirm';

interface OrderRow {
  id: string; number: string; status: string; ordered_at: string; promised_date: string | null;
  on_hold: boolean; hold_reason: string | null;
  customer_id: string; customer_name: string; shipping_label: string | null; shipping_city: string | null;
  total_qty: number; total_value: number; released_units: number;
}

interface Customer { id: string; name: string; code: string; type: string }
interface CustomerShippingAddress { id: string; label: string; city: string; postal_code: string; address: string; is_default: boolean }
interface CustomerDetail extends Customer {
  addresses: CustomerShippingAddress[];
  /** Forma de pago de la ficha: se propone en el pedido y se puede cambiar. */
  paymentMethod?: { id: string; name: string; days: number } | null;
}
interface ModelOption { id: string; code: string; name: string; width_mm: number; height_mm: number }

interface LineDraft { modelId: string; quantity: string; unitPrice: string }

const TODAY = new Date().toISOString().slice(0, 10);
const inDays = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

/** Estados que la API acepta: los del catálogo más `open` (confirmados + en producción). */
const ESTADOS_VALIDOS = new Set(['', 'open', ...SALES_ORDER_STATUSES]);

export function OrdersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  // `?status=…` viene de los paneles del cuadro de mando: la lista abre ya filtrada, pero
  // solo si el valor existe (uno inventado daba 500 en la API) y se mantiene en la URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const estadoInicial = searchParams.get('status') ?? '';
  const [status, setStatus] = useState(ESTADOS_VALIDOS.has(estadoInicial) ? estadoInicial : '');
  useEffect(() => {
    const enUrl = searchParams.get('status') ?? '';
    setStatus(ESTADOS_VALIDOS.has(enUrl) ? enUrl : '');
  }, [searchParams]);
  const cambiarEstado = (valor: string) => {
    setStatus(valor);
    setSearchParams((params) => {
      if (valor) params.set('status', valor);
      else params.delete('status');
      return params;
    }, { replace: true });
  };
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [holdTarget, setHoldTarget] = useState<{ id: string; number: string } | null>(null);
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; number: string } | null>(null);
  const { confirm, dialog: dialogConfirmacion } = useConfirm();

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['order'] });
  };

  const accion = async (id: string, ruta: string, cuerpo?: Record<string, unknown>) => {
    try {
      await api(`/sales-orders/${id}/${ruta}`, { method: 'POST', body: cuerpo ?? {} });
      refrescar();
      return true;
    } catch (error) {
      toast.error(errorMessage(error));
      return false;
    }
  };

  const confirmar = async (id: string) => {
    if (await accion(id, 'confirm')) toast.success('Pedido confirmado');
  };

  const levantar = async (id: string) => {
    if (await accion(id, 'unhold')) toast.success('Retención levantada');
  };

  // Cancelar abre la ventana flotante con el motivo: antes era un `prompt` del navegador, que no
  // se puede mover y tapa el pedido que el usuario está mirando.
  const cancelar = (row: OrderRow) => {
    if (holdTarget) setHoldTarget(null);
    setCancelTarget({ id: row.id, number: row.number });
  };

  const eliminar = async (row: OrderRow) => {
    if (!(await confirm({
      title: `Borrar el pedido ${row.number}`,
      description: 'Solo se puede si sigue en borrador. Esta acción no se deshace.',
      confirmLabel: 'Borrar pedido', destructive: true
    }))) return;
    try {
      await api(`/sales-orders/${row.id}`, { method: 'DELETE' });
      refrescar();
      toast.success('Pedido borrado');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  useEffect(() => { setPage(1); }, [debounced, status]);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (debounced) params.set('q', debounced);
  if (status) params.set('status', status);

  const ordersQuery = useQuery({
    queryKey: ['orders', page, debounced, status],
    queryFn: () => api<Paginated<OrderRow>>(`/sales-orders?${params.toString()}`)
  });

  // El listado de clientes está paginado: un `<select>` con la primera página dejaría fuera a partir del
  // cliente 51 sin avisar. Se busca en el servidor y se acota la lista; si hay más, se dice.
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const busquedaClienteDebounced = useDebounced(busquedaCliente, 300);
  const customersQuery = useQuery({
    queryKey: ['customers-options', busquedaClienteDebounced],
    queryFn: () => api<Paginated<Customer>>(
      `/customers?pageSize=20${busquedaClienteDebounced ? `&q=${encodeURIComponent(busquedaClienteDebounced)}` : ''}`
    ),
    placeholderData: keepPreviousData,
    enabled: createOpen
  });
  const modelsQuery = useQuery({ // Clave propia: la edición pide la lista completa y no puede pisar la del alta.
    queryKey: ['models', 'active'], queryFn: () => api<{ data: ModelOption[] }>('/mirror-models?active=true') });

  const columns: Column<OrderRow>[] = [
    {
      key: 'number',
      header: 'Pedido',
      cell: (row) => <div><p className="font-mono text-xs font-medium">{row.number}</p><p className="text-[10px] text-muted-foreground">{formatDate(row.ordered_at)}</p></div>
    },
    { key: 'customer', header: 'Cliente', cell: (row) => <span className="font-medium">{row.customer_name}</span> },
    { key: 'shipping', header: 'Envío', cell: (row) => <div className="text-[11px]"><p>{row.shipping_label ?? 'Facturación'}</p><p className="text-muted-foreground">{row.shipping_city ?? '—'}</p></div> },
    {
      key: 'status',
      header: 'Estado',
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusBadge status={row.status} />
          {row.on_hold ? (
            <span title={row.hold_reason ?? undefined}><Badge tone="warning">Retenido</Badge></span>
          ) : null}
        </div>
      )
    },
    { key: 'promised', header: 'Entrega', cell: (row) => <span className="flex items-center gap-1 text-[11px]"><CalendarDays className="size-3 text-muted-foreground" /> {formatDate(row.promised_date)}</span> },
    { key: 'qty', header: 'Cantidad', cell: (row) => <span className="tabular-nums">{formatQty(row.total_qty, 0)} ud</span> },
    { key: 'released', header: 'Liberadas', cell: (row) => <Badge tone={row.released_units >= row.total_qty ? 'success' : row.released_units > 0 ? 'warning' : 'neutral'}>{row.released_units} / {formatQty(row.total_qty, 0)}</Badge> },
    { key: 'value', header: 'Valor', cell: (row) => <span className="font-medium tabular-nums">{formatMoney(row.total_value)}</span> },
    {
      key: 'actions',
      header: '',
      cell: (row) => {
        const editable = ['draft', 'confirmed'].includes(row.status);
        const retenible = !['cancelled', 'shipped'].includes(row.status);
        return (
          <div className="flex items-center justify-end gap-1">
            {can('orders.write') && editable ? (
              <Button variant="ghost" size="icon-sm" title="Editar" onClick={(e) => { e.stopPropagation(); setEditTarget(row.id); }}><Pencil /></Button>
            ) : null}
            {can('orders.write') && row.status === 'draft' && !row.on_hold ? (
              <Button variant="ghost" size="icon-sm" title="Confirmar" onClick={(e) => { e.stopPropagation(); void confirmar(row.id); }}><CheckCircle2 /></Button>
            ) : null}
            {can('orders.write') && retenible ? (
              row.on_hold
                ? <Button variant="ghost" size="icon-sm" title="Levantar retención" onClick={(e) => { e.stopPropagation(); void levantar(row.id); }}><PlayCircle /></Button>
                : <Button variant="ghost" size="icon-sm" title="Retener" onClick={(e) => { e.stopPropagation(); setCancelTarget(null); setHoldTarget({ id: row.id, number: row.number }); }}><PauseCircle /></Button>
            ) : null}
            {can('orders.write') && !['cancelled', 'shipped'].includes(row.status) ? (
              <Button variant="ghost" size="icon-sm" title="Cancelar" onClick={(e) => { e.stopPropagation(); void cancelar(row); }}><XCircle /></Button>
            ) : null}
            {can('orders.write') && row.status === 'draft' ? (
              <Button variant="ghost" size="icon-sm" title="Borrar" onClick={(e) => { e.stopPropagation(); void eliminar(row); }}><Trash2 /></Button>
            ) : null}
            {/* El PDF lo sirve la API en línea: se abre en otra pestaña, sin salir del listado. */}
            <Button variant="ghost" size="icon-sm" title="PDF" onClick={(e) => {
              e.stopPropagation();
              window.open(`/api/sales-orders/${row.id}/pdf`, '_blank', 'noopener');
            }}><FileText /></Button>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/orders/${row.id}`); }}>Abrir</Button>
          </div>
        );
      }
    }
  ];

  return (
    <div>
      <div className={createOpen ? 'hidden' : ''}>
      <PageHeader
        title="Pedidos de venta"
        description="Demanda confirmada, hitos de entrega y liberación de unidades"
        actions={can('orders.write') ? <Button onClick={() => setCreateOpen(true)}><Plus /> Nuevo pedido</Button> : null}
      />
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar pedido o cliente…" className="w-full sm:w-72" />
          <Select value={status} onChange={(e) => cambiarEstado(e.target.value)} className="w-auto min-w-40">
            <option value="">Todos los estados</option>
            <option value="open">Abiertos (confirmados y en producción)</option>
            {SALES_ORDER_STATUSES.map((value) => (
              <option key={value} value={value}>
                {SALES_ORDER_STATUS_LABELS[value] ?? value}
              </option>
            ))}
          </Select>
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground"><ShoppingCart className="size-3" /> {ordersQuery.data?.meta.total ?? 0} pedidos</div>
        </div>
        {ordersQuery.error ? <ErrorState message={(ordersQuery.error as Error).message} onRetry={() => void ordersQuery.refetch()} /> : (
          <DataTable
            columns={columns} rows={ordersQuery.data?.data ?? []} loading={ordersQuery.isLoading}
            rowKey={(row) => row.id} onRowClick={(row) => navigate(`/orders/${row.id}`)}
            empty={<EmptyState title="Sin pedidos de venta" description="Crea el primer pedido de un cliente." icon={ShoppingCart} />}
            footer={<Pagination page={page} totalPages={ordersQuery.data?.meta.totalPages ?? 1} total={ordersQuery.data?.meta.total ?? 0} onPageChange={setPage} />}
          />
        )}
      </Card>
      </div>

      <HoldDialog
        // Al pasar de un pedido a otro sin cerrar, se remonta: el foco vuelve al motivo del nuevo
        // y Escape lo cierra, igual que en las confirmaciones.
        key={holdTarget?.id ?? 'ninguno'}
        orderId={holdTarget?.id ?? null}
        number={holdTarget?.number ?? ''}
        open={Boolean(holdTarget)}
        onOpenChange={(abierto) => { if (!abierto) setHoldTarget(null); }}
        onDone={() => { setHoldTarget(null); refrescar(); }}
      />

      {dialogConfirmacion}

      <CancelOrderDialog
        orderId={cancelTarget?.id ?? null}
        number={cancelTarget?.number ?? ''}
        open={Boolean(cancelTarget)}
        onOpenChange={(abierto) => { if (!abierto) setCancelTarget(null); }}
        onDone={() => { setCancelTarget(null); refrescar(); }}
      />

      <EditOrderDialog
        orderId={editTarget}
        open={Boolean(editTarget)}
        onOpenChange={(abierto) => { if (!abierto) setEditTarget(null); }}
        onDone={() => { setEditTarget(null); refrescar(); }}
      />

      <CreateOrderModal
        open={createOpen} onOpenChange={setCreateOpen}
        customers={customersQuery.data?.data ?? []}
        clientesTotal={customersQuery.data?.meta.total ?? 0}
        busquedaCliente={busquedaCliente}
        onBuscarCliente={setBusquedaCliente}
        models={modelsQuery.data?.data ?? []}
        onSaved={async (id) => {
          setCreateOpen(false);
          await qc.invalidateQueries({ queryKey: ['orders'] });
          navigate(`/orders/${id}`);
        }}
      />
    </div>
  );
}

function CreateOrderModal({ open, onOpenChange, customers, clientesTotal, busquedaCliente, onBuscarCliente, models, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void;
  customers: Customer[]; clientesTotal: number; busquedaCliente: string; onBuscarCliente: (texto: string) => void;
  models: ModelOption[]; onSaved: (id: string) => void;
}) {
  const [customerId, setCustomerId] = useState('');
  // El cliente elegido se guarda entero: al buscar, la lista se acota y el elegido puede desaparecer de
  // ella; sin esto el desplegable mostraba «Selecciona…» mientras el pedido seguía apuntando a ese cliente.
  const [clienteElegido, setClienteElegido] = useState<Customer | null>(null);
  const [shippingAddressId, setShippingAddressId] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [promisedDate, setPromisedDate] = useState(inDays(30));
  const [orderedAt, setOrderedAt] = useState(TODAY);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ modelId: '', quantity: '1', unitPrice: '0' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCustomerId(''); setClienteElegido(null); onBuscarCliente(''); setShippingAddressId(''); setPaymentMethodId(''); setPromisedDate(inDays(30)); setOrderedAt(TODAY); setNotes('');
    setLines([{ modelId: '', quantity: '1', unitPrice: '0' }]); setError(null);
  }, [open]);

  const customerDetail = useQuery({
    queryKey: ['customer-detail', customerId],
    queryFn: () => api<CustomerDetail>(`/customers/${customerId}`),
    enabled: open && Boolean(customerId)
  });
  const shippingAddresses = customerDetail.data?.addresses ?? [];

  useEffect(() => {
    if (!customerId) { setShippingAddressId(''); return; }
    if (!customerDetail.data) return;
    const preferred = customerDetail.data.addresses.find((address) => address.is_default) ?? customerDetail.data.addresses[0];
    setShippingAddressId(preferred?.id ?? '');
  }, [customerId, customerDetail.data]);

  // Formas de pago de cobro y propuesta de la ficha del cliente: se rellena sola y se puede cambiar.
  const formasQuery = useQuery({
    queryKey: ['payment-methods', 'cobros'],
    queryFn: () => api<{ data: { id: string; name: string }[] }>('/payment-methods?scope=cobros'),
    enabled: open,
    staleTime: 10 * 60 * 1000
  });
  const formasPago = formasQuery.data?.data ?? [];
  useEffect(() => {
    const deLaFicha = customerDetail.data?.paymentMethod?.id ?? '';
    setPaymentMethodId(deLaFicha && formasPago.some((forma) => forma.id === deLaFicha) ? deLaFicha : '');
  }, [customerDetail.data, formasPago]);

  const updateLine = (index: number, key: keyof LineDraft, value: string) => {
    setLines((current) => current.map((line, i) => i === index ? { ...line, [key]: value } : line));
  };

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (!customerId) throw new Error('Selecciona un cliente');
      const valid = lines.filter((line) => line.modelId && Number(line.quantity) > 0);
      if (!valid.length) throw new Error('Añade al menos una línea');
      if (shippingAddresses.length > 0 && !shippingAddressId) throw new Error('Selecciona una dirección de envío del cliente');
      const result = await api<{ id: string }>('/sales-orders', {
        method: 'POST',
        body: cuerpoPedidoVenta({
          customerId, shippingAddressId, paymentMethodId, orderedAt, promisedDate, notes,
          lineas: valid.map((line) => ({
            modelId: line.modelId, quantity: Number(line.quantity), unitPrice: Number(line.unitPrice)
          }))
        }) as unknown as Record<string, unknown>
      });
      toast.success('Pedido creado en borrador');
      onSaved(result.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="lg" title="Nuevo pedido de cliente"
      description="Cada línea genera un hito de entrega con la fecha prometida."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Crear pedido</Button></>}
    >
      <div className="space-y-4">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Cliente" required className="sm:col-span-1"
            hint={clientesTotal > customers.length
              ? `Mostrando ${customers.length} de ${clientesTotal}: escribe para acotar`
              : undefined}>
            <Input value={busquedaCliente} onChange={(e) => onBuscarCliente(e.target.value)}
              placeholder="Buscar cliente por nombre, CIF o código…" className="mb-2" />
            <Select value={customerId} onChange={(e) => {
              setCustomerId(e.target.value);
              setClienteElegido(customers.find((c) => c.id === e.target.value)
                ?? (clienteElegido?.id === e.target.value ? clienteElegido : null));
            }}>
              <option value="">Selecciona…</option>
              {clienteElegido && !customers.some((c) => c.id === clienteElegido.id) ? (
                <option value={clienteElegido.id}>{clienteElegido.name} (elegido)</option>
              ) : null}
              {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </Select>
          </Field>
          <Field label="Fecha pedido"><Input type="date" value={orderedAt} onChange={(e) => setOrderedAt(e.target.value)} /></Field>
          <Field label="Entrega prometida"><Input type="date" value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} /></Field>
          <Field label="Forma de pago" hint={
            !customerDetail.data ? 'Si no eliges, se usa la del cliente'
              : !customerDetail.data.paymentMethod ? 'Este cliente no tiene forma de pago en su ficha'
              : formasQuery.isSuccess && !formasPago.some((forma) => forma.id === customerDetail.data?.paymentMethod?.id)
                ? `La forma del cliente (${customerDetail.data.paymentMethod.name}) no está disponible; elige otra`
                : `Propuesta: ${customerDetail.data.paymentMethod.name} (la del cliente). Puedes cambiarla`}>
            <Select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}>
              <option value="">La del cliente</option>
              {formasPago.map((forma) => <option key={forma.id} value={forma.id}>{forma.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field
          label="Dirección de envío"
          className="sm:col-span-3"
          hint={shippingAddresses.length === 0 ? 'Este cliente no tiene direcciones de envío: se usará la dirección de facturación.' : 'Se guardará una copia en el pedido para que cambios posteriores no alteren la trazabilidad.'}
        >
          <Select value={shippingAddressId} onChange={(e) => setShippingAddressId(e.target.value)}>
            {shippingAddresses.length === 0 ? <option value="">Dirección de facturación</option> : null}
            {shippingAddresses.map((address) => (
              <option key={address.id} value={address.id}>
                {address.label} · {address.city}{address.is_default ? ' (predeterminada)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold">Líneas</p>
            <Button variant="outline" size="sm" onClick={() => setLines((current) => [...current, { modelId: '', quantity: '1', unitPrice: '0' }])}><Plus /> Añadir</Button>
          </div>
          <div className="space-y-2">
            {lines.map((line, index) => (
              <div key={index} className="grid grid-cols-[1fr_90px_110px_36px] items-end gap-2">
                <Field label={index === 0 ? 'Modelo' : undefined}>
                  <Select value={line.modelId} onChange={(e) => updateLine(index, 'modelId', e.target.value)}>
                    <option value="">Selecciona…</option>
                    {models.map((model) => <option key={model.id} value={model.id}>{model.code} · {model.name}</option>)}
                  </Select>
                </Field>
                <Field label={index === 0 ? 'Cantidad' : undefined}><Input type="number" min="1" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} /></Field>
                <Field label={index === 0 ? 'Precio ud.' : undefined}><Input type="number" step="0.01" value={line.unitPrice} onChange={(e) => updateLine(index, 'unitPrice', e.target.value)} /></Field>
                <Button variant="ghost" size="icon-sm" onClick={() => setLines((current) => current.length > 1 ? current.filter((_, i) => i !== index) : current)}><Trash2 /></Button>
              </div>
            ))}
          </div>
        </div>
        <Field label="Notas"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></Field>
      </div>
    </InlineView>
  );
}
